export const AUDIENCES = [
  {
    slug: "life-sciences",
    name: "Life Sciences",
    tagline: "Research-grade computation, production-grade reliability",
    description:
      "From early discovery to real-world evidence, we give life-science teams the computational backbone to move faster — validated pipelines, governed data platforms, and analytics that stand up to regulatory scrutiny.",
    image: "https://images.unsplash.com/photo-1643780668909-580822430155?auto=format&fit=crop&w=1600&q=80",
    focus: [
      "Bioinformatics and genomics pipelines for research programmes",
      "Governed research data platforms and lakes",
      "Real-world evidence and outcomes analytics",
      "Translational informatics from bench to bedside",
    ],
    services: ["bioinformatics", "genomics", "healthcare-analytics", "personalised-healthcare"],
  },
  {
    slug: "pharmaceuticals",
    name: "Pharmaceuticals",
    tagline: "Trial intelligence and real-world data for pharma teams",
    description:
      "Pharma runs on evidence. We help sponsors and medical affairs teams find trials, understand landscapes, and generate insight — through our products Clinixify and PatExcite, and bespoke analytics engagements.",
    image: "https://images.unsplash.com/photo-1644088379091-d574269d422f?auto=format&fit=crop&w=1600&q=80",
    focus: [
      "Clinical trial search and intelligence via Clinixify",
      "Consented real-world patient data via PatExcite",
      "Portfolio and pipeline analytics for decision-makers",
      "Pharmacogenomics and biomarker strategy support",
    ],
    services: ["healthcare-analytics", "genomics", "digital-transformation", "bioinformatics"],
  },
  {
    slug: "medtech",
    name: "Medical Devices",
    tagline: "Connected devices, compliant data, clinical integration",
    description:
      "Devices generate the data; we make it useful. Interoperability, remote monitoring pipelines, and regulatory-ready data flows that connect medical device products into real clinical workflows.",
    image: "https://images.pexels.com/photos/6129679/pexels-photo-6129679.jpeg?auto=compress&cs=tinysrgb&w=1600",
    focus: [
      "Device data integration and remote monitoring pipelines",
      "HL7 FHIR and standards-based interoperability",
      "Regulatory-ready data governance and audit trails",
      "Companion apps and clinician-facing dashboards",
    ],
    services: ["patient-care", "digital-transformation", "healthcare-analytics", "clinical-solutions"],
  },
  {
    slug: "health-systems",
    name: "Health Systems",
    tagline: "Enterprise clinical and operational transformation",
    description:
      "Hospitals and health networks partner with us to modernise the core — clinical systems, operational command centres, patient experience, and the interoperability layer that ties it all together.",
    image: "https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1600&q=80",
    focus: [
      "EMR optimisation and clinical workflow redesign",
      "Real-time operational command centres",
      "Patient journey and experience transformation",
      "Enterprise interoperability and data platforms",
    ],
    services: ["clinical-solutions", "patient-experience", "digital-transformation", "healthcare-analytics"],
  },
  {
    slug: "labs",
    name: "Labs",
    tagline: "From sample to signed-out report, faster",
    description:
      "Diagnostic and genomic labs trust us with the pipeline layer — LIS integration, NGS analysis, QC automation, and reporting workflows that cut turnaround without cutting corners.",
    image: "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=1600&q=80",
    focus: [
      "NGS and molecular diagnostics pipelines",
      "Laboratory information system integration",
      "Automated QC and validation frameworks",
      "Clinical reporting portals for referring physicians",
    ],
    services: ["bioinformatics", "genomics", "clinical-solutions", "healthcare-analytics"],
  },
  {
    slug: "patients",
    name: "Patients",
    tagline: "Healthcare that revolves around the person",
    description:
      "Everything we build ultimately serves the patient. Through PatExcite, personalised care journeys, and humane digital experiences, we make healthcare feel less like a system and more like care.",
    image: "/assets/patient-experience.jpeg",
    focus: [
      "PatExcite — your digital twin for precision health",
      "Personalised prevention and care plans",
      "Virtual care and continuous remote monitoring",
      "Simple, accessible digital front doors",
    ],
    services: ["patient-care", "patient-experience", "personalised-healthcare", "cost-effective-engagement"],
  },
  {
    slug: "hospitals",
    name: "Hospitals",
    tagline: "AI-native operations for the modern hospital",
    description:
      "Hospitals run on thin margins and thick complexity. We bring AI-native clinical systems, operational intelligence, and patient experience tooling that cuts cost-to-serve while lifting quality of care — delivered in weeks, not years.",
    image: "https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1600&q=80",
    focus: [
      "Clinical workflow digitisation and EMR optimisation",
      "Real-time operational command centres",
      "Patient experience and engagement journeys",
      "Cost-effective, AI-led support and maintenance",
    ],
    services: ["clinical-solutions", "patient-care", "healthcare-analytics", "cost-effective-engagement"],
  },
  {
    slug: "payers",
    name: "Payers",
    tagline: "Claims, risk and member experience, intelligently automated",
    description:
      "Payers sit on oceans of claims and member data. Our AI-native analytics turn that into sharper risk stratification, faster claims intelligence, and member experiences that cost less and feel better.",
    image: "https://images.unsplash.com/photo-1644088379091-d574269d422f?auto=format&fit=crop&w=1600&q=80",
    focus: [
      "Claims analytics and processing intelligence",
      "Risk stratification and population health models",
      "Member engagement and digital experience",
      "Fraud, waste and abuse signal detection",
    ],
    services: ["healthcare-analytics", "digital-transformation", "patient-experience", "cost-effective-engagement"],
  },
  {
    slug: "academia",
    name: "Academia",
    tagline: "Research computing without the infrastructure burden",
    description:
      "Universities and research institutes get publication-grade computational infrastructure without building it: bioinformatics pipelines, genomics at scale, and the GENEiuX AI builder for grant-funded teams.",
    image: "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=1600&q=80",
    focus: [
      "Reproducible research pipelines and workflows",
      "Genomics and multi-omics analysis for studies",
      "GENEiuX platform access for research teams",
      "Grant-ready data platforms and compliance",
    ],
    services: ["bioinformatics", "genomics", "healthcare-analytics", "personalised-healthcare"],
  },
  {
    slug: "government",
    name: "Government",
    tagline: "Population health infrastructure for public systems",
    description:
      "Public health demands scale, sovereignty, and trust. We build population-scale genomics, registries, and health analytics platforms that are compliant, auditable, and affordable for public systems.",
    image: "https://images.pexels.com/photos/6129679/pexels-photo-6129679.jpeg?auto=compress&cs=tinysrgb&w=1600",
    focus: [
      "Population-scale genomics and screening programmes",
      "Public health registries and surveillance analytics",
      "Standards-based interoperability across agencies",
      "Sovereign, compliant, auditable data platforms",
    ],
    services: ["genomics", "healthcare-analytics", "digital-transformation", "clinical-solutions"],
  },
];
